import React, { Component } from 'react'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { connect as connectRedux } from 'react-redux'
import compose from 'recompose/compose'
import { Map, is } from 'immutable'
import _get from 'lodash/get'
import isFunction from 'lodash/isFunction'
import some from 'lodash/some'
import SpotlightBannerV2 from '../SpotlightBannerV2/SpotlightBannerV2'
import SpotlightPlaceholder from '../SpotlightPlaceholder/index'
import SpotlightTrailerBannerV2 from '../SpotlightTrailerBannerV2/index'
import SpotlightPromoBannerV2 from '../SpotlightPromoBannerV2/index'
import {
  createUpstreamContext,
  SCREEN_TYPE_MEMBER_HOME,
} from '../../services/upstream-context/index'
import {
  TYPE_CONTENT_SERIES,
  TYPE_CONTENT_VIDEO,
  TYPE_CONTENT_PROMOTION,
  TYPE_CONTENT_PRODUCT,
} from '../../services/content-type/index'
import { connect as connectStaticText } from '../StaticText/connect'
import { DaytonaCase, DaytonaSwitch, DaytonaDefault } from '../Daytona/index'
import SpotlightAdV2 from '../Daytona/Campaigns/WA-6/SpotlightAdV2/index'
import { MH_SPOTLIGHT_COLLAPSED_KEY } from '../../services/feature-tracking/index'
import { EN } from '../../services/languages/constants'
import { getUserLanguagePrimary } from '../../services/user/index'
import { PM_PLACEMENT_PAYLOAD_TYPE_MERCHANDISING_CONTENT } from '../../services/page-manager/constants'
import {
  selectSeriesById,
  selectVideoById,
  selectPromotionById,
  selectProductById,
} from '../../services/node/index'
import { selectSpotlightSectionById } from '../../services/page-manager/index'
import { getBoundActions } from '../../actions/index'
import { isMemberHome } from '../../services/url/index'
import { getAuthIsLoggedIn } from '../../services/auth/index'
import {
  TYPE_PLACEMENT,
} from '../../services/pm-section/index'

const CONTEXT_TYPE = 'pm/spotlight'

// Delay before showing placeholders - if data arrives within this time, skip placeholders
const PLACEHOLDER_DELAY_MS = 200

function getLabel (placementData, payloadType, staticText) {
  if (payloadType === PM_PLACEMENT_PAYLOAD_TYPE_MERCHANDISING_CONTENT) {
    const label = placementData.get('label', '')
    return label
  }
  return staticText.getIn(['data', 'topVideoForYou'])
}

/**
 * @param {
 *  import('../../services/content-type/index').TYPE_CONTENT_VIDEO |
 *  import('../../services/content-type/index').TYPE_CONTENT_SERIES |
 *  import('../../services/content-type/index').TYPE_CONTENT_PROMOTION |
 * } contentType
 * @param {Object} state redux state
 * @param {Object} params
 * @param {string | number} params.id
 * @param {string} params.language
 * @param {string[]} params.subPath
 * @returns {import('immutable').Map}
 */
function getContentByType (contentType, state, params) {
  switch (contentType) {
    case TYPE_CONTENT_VIDEO:
      return selectVideoById(state, params)
    case TYPE_CONTENT_SERIES:
      return selectSeriesById(state, params)
    case TYPE_CONTENT_PROMOTION:
      return selectPromotionById(state, params)
    case TYPE_CONTENT_PRODUCT:
      return selectProductById(state, params)
    default:
      return Map()
  }
}

class Spotlight extends Component {
  constructor (props) {
    super(props)
    this.placeholderTimeout = null
    this.state = {
      showPlaceholder: false,
    }
  }

  componentWillUnmount = () => {
    this.removeIntersectionObserver()
    this._component = undefined
    if (this.placeholderTimeout) {
      clearTimeout(this.placeholderTimeout)
      this.placeholderTimeout = null
    }
  }

  componentDidMount () {
    const { sectionId } = this.props

    // update contect of Spotlight on every re-mount
    this.props.fetchSection(sectionId)
    this.checkAndSetPlaceholderDelay()
  }

  componentDidUpdate (prevProps) {
    const {
      hasPlacementData,
      hasContentData,
      processing,
      hasSectionTypePlacementData,
    } = this.props
    const prevHasPlacementData = prevProps.hasPlacementData
    const prevHasContentData = prevProps.hasContentData
    const prevProcessing = prevProps.processing
    const prevHasSectionTypePlacementData = prevProps.hasSectionTypePlacementData

    // Check if loading state changed
    const wasLoading = !prevHasPlacementData
      || !prevHasContentData
      || prevProcessing
      || !prevHasSectionTypePlacementData
    const isLoading = !hasPlacementData
      || !hasContentData
      || processing
      || !hasSectionTypePlacementData

    if (wasLoading !== isLoading) {
      this.checkAndSetPlaceholderDelay()
    }
  }

  checkAndSetPlaceholderDelay = () => {
    const {
      hasPlacementData,
      hasContentData,
      processing,
      hasSectionTypePlacementData,
    } = this.props

    // Clear any existing timeout
    if (this.placeholderTimeout) {
      clearTimeout(this.placeholderTimeout)
      this.placeholderTimeout = null
    }

    const isLoading = !hasPlacementData
      || !hasContentData
      || processing
      || !hasSectionTypePlacementData

    if (isLoading) {
      // Set timeout to show placeholder after delay
      this.placeholderTimeout = setTimeout(() => {
        this.setState({ showPlaceholder: true })
        this.placeholderTimeout = null
      }, PLACEHOLDER_DELAY_MS)
    } else {
      // Data arrived - hide placeholder
      this.setState({ showPlaceholder: false })
    }
  }

  /**
   * Set the react dom element when it is created
   * @param {Element} component A DOM element
   */
  onRef = (component) => {
    const { visibilityThreshold, onVisibilityChange } = this.props
    this.removeIntersectionObserver()
    this._component = component
    if (!isFunction(onVisibilityChange) || !component || !global.IntersectionObserver) {
      return
    }
    this._observer = new IntersectionObserver(this.onIntersectionObserverVisibility, {
      // Use viewport
      root: null,
      rootMargin: '0px',
      threshold: visibilityThreshold,
    })
    this._observer.observe(component)
  }

  /**
   * Trigger the onVisibilityChange action when the component
   * is visible
   */
  onIntersectionObserverVisibility = (entries) => {
    const { onVisibilityChange, contentData } = this.props
    if (!isFunction(onVisibilityChange)) {
      return
    }
    // Loop through entires to see if one is visible
    const isVisible = some(entries, (entry) => _get(entry, 'isIntersecting'))
    // Ensure that hasContentData is correctly determined
    const hasContentData = Map.isMap(contentData) && !contentData.isEmpty()
    const spotlightContentData = hasContentData ? contentData : Map()
    onVisibilityChange(isVisible, spotlightContentData)
  }

  /**
   * Remove the intersection observer if it exists
   */
  removeIntersectionObserver = () => {
    if (this._observer && this._component) {
      this._observer.unobserve(this._component)
      this._observer = undefined
    }
  }

  render () {
    const { props } = this
    const {
      app,
      language,
      page,
      auth,
      history,
      hasSectionData,
      hasPlacementData,
      hasContentData,
      contentData,
      pmPlacement,
      payloadType,
      campaignId,
      staticText,
      sectionId,
      sectionIndex,
      itemIndex: index,
      contentId,
      contentType,
      score,
      source,
      filters,
      merchEventId,
      spotlightMinimized,
      showMinimizeButton,
      processing,
      viewMode,
      isMemberHomePage,
      ga4ImpressionEventData,
      hasSectionTypePlacementData,
      hasGraphQLData,
      slug,
      section = Map(),
    } = props
    const mediaId = contentData.getIn(['preview', 'id'])
    const viewModeTrailer = viewMode === 'trailer'
    const classNames = ['section-placement__wrapper']

    if (!hasSectionData) {
      return null
    }

    if (!contentId) {
      return 'error - missing data'
    }

    if (
      mediaId
        && payloadType === PM_PLACEMENT_PAYLOAD_TYPE_MERCHANDISING_CONTENT
        && viewModeTrailer
    ) {
      classNames.push('section-placement__wrapper--trailer')
    }

    const isLoading = !hasPlacementData
      || !hasContentData
      || processing
      || !hasSectionTypePlacementData

    // If loading, show placeholder only after delay has elapsed
    if (isLoading) {
      const shouldShowPlaceholder = this.state.showPlaceholder
      if (!hasSectionTypePlacementData && shouldShowPlaceholder) {
        return (
          <div
            ref={this.onRef}
            className={classNames.join(' ')}
            data-page-manager-section-id={sectionId}
          >
            <SpotlightPlaceholder />
          </div>
        )
      }
      // Container always exists to take up space, but placeholder only shows after delay
      classNames.push('section-placement__wrapper--placeholder')
      return (
        <div
          ref={this.onRef}
          className={classNames.join(' ')}
          data-page-manager-section-id={sectionId}
        >
          {shouldShowPlaceholder ? <SpotlightPlaceholder /> : null}
        </div>
      )
    }

    const label = getLabel(pmPlacement, payloadType, staticText)
    const spotlightType = ga4ImpressionEventData.get('spotlight_type')
    const recommId = Map.isMap(section) ? section.getIn(['data', 'data', 'recommId']) : null

    const rowData = {
      rowNumber: 0,
      rowPlacement: 'Spotlight',
      rowName: ga4ImpressionEventData.get('row_name'),
      ...(recommId && { recommId }),
      ...(isMemberHomePage && { clickAttribution: 'Member Home' }),
      ...(spotlightType && { spotlightType }),
      ...(slug && { spotlightCampaign: slug }),
    }
    let upstreamContext = createUpstreamContext({
      screenType: SCREEN_TYPE_MEMBER_HOME,
      contextType: CONTEXT_TYPE,
      playEventData: rowData,
      mhTileData: rowData,
      campaignId,
      source,
      score,
      sectionId,
      sectionIndex,
      itemIndex: index,
      contentId,
      contentType,
      filters,
      merchEventId,
    })

    if (contentType === TYPE_CONTENT_VIDEO) {
      upstreamContext = upstreamContext.set('videoId', contentData.get('id'))
    }
    if (contentType === TYPE_CONTENT_SERIES) {
      upstreamContext = upstreamContext.set('seriesId', contentData.get('id'))
    }

    let tileBanner = null

    if (spotlightMinimized) {
      tileBanner = (
        <SpotlightPlaceholder />
      )
    } else if (
      mediaId && payloadType === PM_PLACEMENT_PAYLOAD_TYPE_MERCHANDISING_CONTENT && viewModeTrailer
    ) {
      /*
        - override tileBanner with new autoplay banner, but pass default tileBanner through as
          children for fallback purposes at mobile breakpoint
      */
      tileBanner = (
        <SpotlightTrailerBannerV2
          app={app}
          language={language}
          page={page}
          auth={auth}
          label={label}
          data={contentData}
          mediaId={mediaId}
          key="banner-0"
          upstreamContext={upstreamContext}
          showMinimizeButton={showMinimizeButton}
          useGraphQLData={hasGraphQLData}
          section={section}
        >
          <SpotlightBannerV2
            app={app}
            language={language}
            page={page}
            auth={auth}
            upstreamContext={upstreamContext}
            history={history}
            label={label}
            data={contentData}
            mediaId={mediaId}
            contentType={contentType}
            payloadType={payloadType}
            useGraphQLData={hasGraphQLData}
            section={section}
          />
        </SpotlightTrailerBannerV2>
      )
    } else if (
      payloadType === PM_PLACEMENT_PAYLOAD_TYPE_MERCHANDISING_CONTENT
      && (contentType === TYPE_CONTENT_PROMOTION || contentType === TYPE_CONTENT_PRODUCT)
    ) {
      tileBanner = (
        <SpotlightPromoBannerV2
          label={label}
          data={contentData}
          upstreamContext={upstreamContext}
          useGraphQLData={hasGraphQLData}
        />
      )
    } else {
      tileBanner = (
        <SpotlightBannerV2
          app={app}
          language={language}
          page={page}
          auth={auth}
          upstreamContext={upstreamContext}
          history={history}
          label={label}
          data={contentData}
          mediaId={mediaId}
          contentType={contentType}
          payloadType={payloadType}
          useGraphQLData={hasGraphQLData}
          section={section}
        />
      )
    }
    return (
      <div ref={this.onRef} className={classNames.join(' ')} data-page-manager-section-id={sectionId}>
        <DaytonaSwitch>
          <DaytonaCase campaign="WA-6">
            {(campaign, variation, subject) => (
              <SpotlightAdV2
                subject={subject}
                campaign={campaign}
                variation={variation}
              />
            )}
          </DaytonaCase>
          <DaytonaDefault unwrap>
            {tileBanner}
          </DaytonaDefault>
        </DaytonaSwitch>
      </div>
    )
  }
}

Spotlight.propTypes = {
  onVisibilityChange: PropTypes.func,
  visibilityThreshold: PropTypes.number,
  hasSectionTypePlacementData: PropTypes.bool,
  graphQLData: ImmutablePropTypes.map,

}

Spotlight.defaultProps = {
  visibilityThreshold: 0.80,
  graphQLData: Map(),
}

function spotlightPropsAreEqual (prevProps, nextProps) {
  const prevHasContentData = _get(prevProps, 'hasContentData')
  const nextHasContentData = _get(nextProps, 'hasContentData')
  const prevSpotlightMinimized = _get(prevProps, 'spotlightMinimized')
  const nextSpotlightMinimized = _get(nextProps, 'spotlightMinimized')

  // contentData must participate in equality: hasContentData can stay true when GraphQL
  // replaces Redux data (e.g. learnerLevelLabel) and a shallow memo would skip the update.
  return prevHasContentData === nextHasContentData
    && prevSpotlightMinimized === nextSpotlightMinimized
    && prevProps.hasGraphQLData === nextProps.hasGraphQLData
    && is(prevProps.contentData, nextProps.contentData)
}

const MemoizedSpotlight = React.memo(Spotlight, spotlightPropsAreEqual)

export default compose(
  connectStaticText({ storeKey: 'memberHome' }),
  connectRedux(
    (state, props) => {
      const {
        app,
        page,
        auth,
        user,
        resolver,
        pmSection,
      } = state
      const { sectionId, graphQLData } = props
      const section = pmSection.get(sectionId)
      const language = getUserLanguagePrimary(user) || EN

      const {
        hasSectionData,
        pmPlacementData,
        payloadType,
        contentType,
        contentId,
        hasPlacementData,
        campaignId,
        merchEventId,
        source,
        score,
        processing,
        viewMode,
        slug,
      } = selectSpotlightSectionById(state, sectionId)

      const hasGraphQLData = Map.isMap(graphQLData) && graphQLData.size > 0 && graphQLData.get('id')
      const contentData = hasGraphQLData ? graphQLData : getContentByType(contentType, state, {
        id: contentId,
        language,
        subPath: ['data'],
      })
      const hasContentData = Map.isMap(contentData) && !contentData.isEmpty()
      const isLoggedIn = getAuthIsLoggedIn(auth)
      const isMemberHomePage = isMemberHome(resolver.get('path') || '/', isLoggedIn)
      const sectionTypePlacement = pmSection.find((s) => s.getIn(['data', 'type']) === TYPE_PLACEMENT) || Map()
      const hasSectionTypePlacementData = Map.isMap(sectionTypePlacement)
        && sectionTypePlacement.size > 0

      return {
        app,
        page,
        auth,
        hasSectionData,
        hasPlacementData,
        hasContentData,
        campaignId,
        contentData: contentData || Map(),
        payloadType,
        pmPlacement: pmPlacementData || Map(),
        language,
        contentType,
        contentId,
        merchEventId,
        source,
        score,
        spotlightMinimized: state.featureTracking.getIn([
          'data',
          MH_SPOTLIGHT_COLLAPSED_KEY,
        ]),
        sectionId,
        processing,
        viewMode,
        isMemberHomePage,
        hasGraphQLData: !!hasGraphQLData,
        hasSectionTypePlacementData,
        slug,
        section,
      }
    },
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        fetchSection: actions.pageManagerActions.fetchSection,
      }
    },
  ),
)(MemoizedSpotlight)
